import type { Option } from "@tento-lona/sheets";
import { DateTime, NaiveDate, SemanticTime, Utc } from "@tento-chrono";
import { RangeBasedFetcher } from "@tento-core/range-based-fetcher";
import { ListenerErrors, type ListenerErrorSink } from "@tento-core/listener-errors";
import { Tracing } from "@tento-core/tracing";
import type { TypedApi } from "@tento-core/typed-api";
import { CellId, type CellValue, Json, type JsonObject, RowNetworkKey } from "@tento-lona/sheets";
import { type CellCardinality, EncodingRegistry } from "@tento-lona/sheets/platform";
import { Actor, type InvalidationSink } from "../invalidation.ts";
import { Network } from "../network.ts";
import type { CellEntry, FetchedCells, TimepointCellEntry, Unsubscribe } from "@tento-lona/sheets";
import { RowId } from "@tento-lona/sheets";
import { RawSheet } from "@tento-lona/sheets";
import { RowSyncState } from "./sync-poll-entry.ts";
import { RowData } from "./row-data.ts";
import { type RowCellsChange, RowSubscriptions } from "./row-subscriptions.ts";
import { CellAuthority, type HeldCellStore, type StoreChange } from "./cell-authority.ts";
import type { CellWriteHost } from "./cell-writes.ts";
import type { CellAddress, LocalCellValue } from "./row-data.ts";
import type { EventsTable } from "../indices/events-table.ts";
import { RowFetchBindings } from "./row-cache-bindings.ts";
import {
  requireDateTimeRange,
  requireNaiveDateRange,
  requireNonEmptyKey,
  requireRowId,
} from "./_contracts.ts";

/**
 * The subset of the Lona client that index factories may access.
 * Kept narrow to avoid pulling in the full client graph.
 */
export interface ClientContext {
  readonly api: TypedApi<Network.Routes>;
  readonly invalidation: InvalidationSink;
  readonly encodings: EncodingRegistry;
  readonly rowMeta?: (rowKey: RowNetworkKey) => Option<Network.SheetRow>;
  readonly openRowHandle?: (
    rowKey: RowNetworkKey,
    rowType: Option<string>,
  ) => Promise<RowCellCache>;
  /** Client-scoped event storage, shared by every `EventsRowBinding`
   *  on this client. See `indices/events-table.ts`. Constructed
   *  once per `LonaSdk.Client`; index factories read it from ctx. */
  readonly eventsTable?: EventsTable;
}

/** SDK-private response commit: install and finish coverage in one synchronous notification burst. */
export type RowFetchCompletion = (
  cells: Network.SheetCell[],
  install: () => void,
) => void;

/** Existing one-argument fetches remain callable and assignable. */
type RowFetch = {
  (range: DateTime.Range<Utc>): Promise<Network.SheetCell[]>;
  (range: DateTime.Range<Utc>, complete: RowFetchCompletion): Promise<Network.SheetCell[]>;
};

type IndexFactory<O> = (
  handle: RowCellCache<Record<string, unknown>>,
  cells: CellEntry[],
) => O;

/**
 * Per-fire metadata passed to `onDataWithStage` alongside the cell. The
 * actor is the originator of the local write that produced this cell
 * value, or `null` if no local write stamp is present (sync-driven
 * updates, initial loads, formula computes). `CellHandle.onData`
 * forwards this to plugin callbacks as their second argument so plugins
 * can opt out of self-echo rebinds.
 */
export type DataFireChange = {
  readonly actor: Option<Actor>;
};

export type SubscribeCallbacks = {
  onPlaceholderData: () => void;
  onDataWithStage: (
    cell: Option<CellEntry>,
    syncState: RowSyncState,
    change: DataFireChange,
  ) => void;
  onDataFull: (cell: Option<CellEntry>, change: DataFireChange) => void;
};

export type SubscribeHandle = {
  /** The column's id (`<rowId>:<column>`). Every reader of the same column
   *  shares it; it does not identify the subscription. */
  readonly id: string;
  dispose(): void;
  /** Fire-and-forget: a failure is traced, never thrown. Do NOT await in
   *  bindCell — see design doc. */
  fetch(): void;
  /** The same fetch, awaitable: resolves once the column's data is loaded
   *  (at once when it is cached) and rejects with the row request's error. */
  fetchChecked(): Promise<void>;
};

type SubscribeOptions = {
  subscribeToChanges?: (evaluate: () => void) => () => void;
};

function cellEncodingFromMetadata(metadata: Option<Json>): Option<string> {
  const rowMetadata = Json.object(metadata);
  const encoding = Json.string(rowMetadata, "encoding");
  if (encoding != null) return encoding;
  const shape = Json.string(rowMetadata, "shape");
  if (shape != null) return shapeToEncoding(shape);
  return null;
}

const SHAPE_TO_ENCODING: ReadonlyMap<string, string> = new Map([
  ["task/v1", "task.v1"],
  ["event/v1", "event.v1"],
  ["weather/v1", "weather.v1"],
  ["slot/v1", "slot.v1"],
]);

/** The actor an untagged store change is copied in with: a singleton no plugin holds (self-echo never matches it). */
const STORE_ACTOR = new Actor("store-view");

function shapeToEncoding(shape: string): Option<string> {
  return SHAPE_TO_ENCODING.get(shape) ?? null;
}

/** Set by `RowCellCache`'s static block (a white-box door): `rowCellCacheStoreOf`'s target. */
let fetchedStoreOf: (rh: RowCellCache) => RowData;

/**
 * The fetched-cell store behind `rh` — a white-box door for this repository's
 * tests (re-exported by `@tento-lona/internal-testing` only), which feed it
 * partial wire cells; not on a published entry.
 */
export function rowCellCacheStoreOf(rh: RowCellCache): RowData {
  return fetchedStoreOf(rh);
}

/**
 * Per-row reactive handle: sync reads (`cellsIn`, `getAllInRange`), an async
 * refresh path (`ensureFresh`, `getCells`), fetch coverage and subscriptions.
 *
 * While an open sheet holds the row, its cells live in ONE store — the
 * sheet's rows-side cell store, or the client's canonical store for a
 * canonical key — and this cache is a VIEW over it (`CellStoreView`, wired by
 * the holder, `RowFetchBindings`): every read answers the store's cells, every
 * local write is a store write, a server read this cache fetched installs
 * into the store, and the store's change feed fires this cache's listeners
 * with the writer's actor (`storeChanged`). Only a row no sheet holds keeps
 * its cells here (`RowData`).
 *
 * `withIndex` is an internal rebuild-hook mechanism used by `EventsTable.
 * attachRow` to subscribe to cell-array updates. Sheets-side
 * `FacadeRegistry` is the public read surface — see `row.as(KEY)`.
 */
export class RowCellCache<
  Indices extends Record<string, unknown> = Record<never, never>,
> {
  static {
    fetchedStoreOf = (rh) => rh.#fetched;
  }

  kind: "raw" | "formula" | "series" | "formula-series" = "raw";
  lookupKey: Option<string> = null;
  /** The row's attribute bag, as the server last sent it. */
  attributes: Option<JsonObject> = null;
  ephemeral: boolean = false;
  isStale: boolean = false;
  #staleGeneration = 0;
  #resetStaleGeneration = -1;
  syncState: Option<RowSyncState> = null;
  /** Single-slot hook owned by the sheet cell accessor; app code uses
   *  `onSyncState(listener)`, which supports many subscribers. */
  onSyncStateChanged: Option<() => void> = null;

  /**
   * @internal When set, the RowCellCache belongs to a sheet and delegates fetch
   * to the sheet's bulk cell fetch (CellAccessor.fetch). Standalone
   * RowHandles leave this null and fetch per-row via the RangeBasedFetcher.
   */
  sheetFetch: Option<(range: NaiveDate.Range) => Promise<void>> = null;

  /**
   * @internal When set, invalidateData() also resets the sheet-level fetch
   * cache so the next bulk fetch hits the server (and returns fresh
   * syncKeys). Wired by CellAccessor when creating the RowCellCache.
   */
  invalidateSheetCache: Option<() => void> = null;

  /** Who hears this row's changes: change, range and sync-state listeners, one token per live reader. */
  readonly #subscriptions: RowSubscriptions = new RowSubscriptions({
    rowLabel: () => this.rowId.toString(),
    listenersChanged: () => this.#reconcileSourceForwarding(),
    listenerErrors: () => this.listenerErrors,
  });

  computeRange: Option<(range: NaiveDate.Range) => Promise<CellEntry[]>> = null;

  setComputeRange(fn: (range: NaiveDate.Range) => Promise<CellEntry[]>): void {
    this.computeRange = fn;
    // A freshly wired or re-wired formula must compute on the next read,
    // even when the sheet-level bulk fetch range is already covered.
    this.#beginStaleGeneration();
  }

  sourceHandle: Option<RowCellCache> = null;

  /** Teardown for `bindSourceHandle`'s listener forwarding, released in
   *  `disposeAll`. Without it a series projection's `onChange` pins the
   *  recycled renderer handle on the long-lived source row forever. */
  #sourceChangeUnsub: Option<() => void> = null;

  /**
   * The row's own cells, for a row no sheet holds (no `#view`). A white-box
   * test feeds partial wire cells straight into it through the white-box door
   * `rowCellCacheStoreOf` (`@tento-lona/internal-testing`).
   */
  readonly #fetched: RowData = new RowData();
  #derived: Option<RowData> = null;

  /**
   * The one store this row's cells live in while a sheet holds it (see the
   * class doc); `null` for a row no sheet holds.
   */
  #view: Option<HeldCellStore> = null;
  /** The row's own cells as an authority: what reads answer while no sheet holds the row. */
  readonly #ownAuthority: CellAuthority = CellAuthority.own({
    fetched: this.#fetched,
    derived: () => this.#derived,
    mergesDerived: () => this.kind === "formula" || this.kind === "formula-series",
  }, this.#writeHost());
  /**
   * Which store answers this row's cells and where a server read lands —
   * chosen once, with `#view`, in `setView` (see `CellAuthority`).
   */
  #authority: CellAuthority = this.#ownAuthority;
  /**
   * Who last wrote each cell while the view is installed (`storeChanged`),
   * keyed as `RowData`'s stamps are: what `lastActorFor` answers.
   */
  readonly #viewActors = new Map<string, Actor>();
  /** Store changes this cache heard during the current delegated write (`#viaStore`). */
  #heard = 0;

  readonly fetcher: RangeBasedFetcher<Utc>;

  readonly #factories = new Map<string, IndexFactory<unknown>>();
  /** Rebuild listeners a fork carries, as `fork()` carries index factories: called with the fork as `handle`. */
  readonly #carriedRebuilt = new Set<(handle: RowCellCache, change: RowCellsChange) => void>();
  readonly #indices: Record<string, unknown> = {};

  /** @internal Per-row fetch function. Overridable by CellAccessor to
   *  resolve canonical keys from the RowLocalId registry at call time. */
  fetchFn: RowFetch;
  /** Fetch transport has already installed through its own snapshot lease. */
  fetchInstallsAuthority = false;
  /**
   * Which open sheets wire this cache's fetch — the accessors of the sheets
   * holding the row (`RowFetchBindings`): a closing sheet hands the wiring to
   * the newest survivor, or back to the backend's own fetch.
   */
  readonly fetchBindings: RowFetchBindings = new RowFetchBindings(this);

  /**
   * @internal One-shot post-apply hook for the NEXT per-row fetch settle.
   * Set by `CellAccessor._makeFetchFn`'s settle branches (lookup-key /
   * canonical-backstop) just before the fetch resolves; the fetcher
   * continuation consumes it AFTER `fetched.applyBatch` + `fireListeners`,
   * so the callback observes the SETTLED cells. (Emitting an invalidation
   * from inside the fetchFn itself would run a microtask BEFORE the apply
   * — synchronous sink subscribers would re-read stale cells.) See
   * .requirements/structural-rebind — rekeyed-row-repaints-on-fetch-settle.
   */
  onNextFetchApplied: Option<() => void> = null;

  /**
   * Server-declared `metadata.encoding` string (e.g. "event.v1").
   * Read by `parseCellData` to select the registered decoder.
   * `null` when the server didn't flag an encoding for this row —
   * cells pass through unchanged.
   */
  encoding: Option<string> = null;

  /**
   * Whether the server marked this row's cells provider-owned
   * (`metadata.readOnly = true`, e.g. a calendar the account's grant cannot
   * write): the server refuses every cell write to it. `false` until
   * metadata is applied.
   */
  readOnly = false;

  /**
   * Apply row metadata to this handle. Reads the server-declared
   * wire-cell `encoding` tag from `row.metadata` and derives
   * standard-vs-custom store routing (`cardinality`): the row's own
   * `attributes.cellCardinality`, else `typeCardinality`, the row type's
   * (`RowType.get(meta.type).cellCardinality`, resolved by the caller — the
   * sync layer does not read the row-type registry). Idempotent: safe to
   * call again whenever metadata updates.
   */
  applyMeta(meta: Network.SheetRowDb, typeCardinality: CellCardinality): void {
    this.lookupKey = meta.lookupKey ?? null;
    this.attributes = Json.object(meta.attributes);
    this.encoding = cellEncodingFromMetadata(meta.metadata);
    this.readOnly = Json.boolean(Json.object(meta.metadata), "readOnly") === true;
    // Named by this cache's own key: a row built from authored input
    // (`SheetRow.from`) carries no wire id, and the id only labels a trace.
    const cardinality = RawSheet.cellCardinality({
      id: RowNetworkKey.fromWire(this.rowId.toString()),
      type: meta.type,
      ...(this.attributes == null ? {} : { attributes: this.attributes }),
      ...(meta.lookupKey == null ? {} : { lookupKey: meta.lookupKey }),
    }, typeCardinality);
    Tracing.log("drive/authority/metadata", { key: this.rowId.toString(), lookupKey: this.lookupKey, type: meta.type, cardinality, attributedCardinality: this.attributes?.cellCardinality ?? null });
    this.#fetched.cardinality = cardinality;
    if (this.#derived) this.#derived.cardinality = cardinality;
  }

  get cardinality(): CellCardinality {
    return this.#fetched.cardinality;
  }

  /**
   * Wire-format decoder registry, set by the backend that owns this
   * handle. `parseCellData` consults it for every inbound cell and
   * decodes in place using the row's `encoding` metadata (if any).
   * Null when constructed outside a backend-managed context — cells
   * pass through unchanged.
   */
  registry: Option<EncodingRegistry> = null;

  /**
   * @internal The host's sink for what this row's listeners threw (each
   * fan-out reports out of band, never into its producer): the owning
   * backend's, which a client takes from its host; `ListenerErrors.RETHROW`
   * when none was named. A fork reports to the same sink.
   */
  readonly listenerErrors: ListenerErrorSink;

  constructor(
    readonly rowId: RowId,
    fetchFn: RowFetch,
    registry: Option<EncodingRegistry>,
    listenerErrors: ListenerErrorSink = ListenerErrors.RETHROW,
  ) {
    requireRowId("RowCellCache.constructor", rowId);
    this.fetchFn = fetchFn;
    this.registry = registry;
    this.listenerErrors = listenerErrors;
    this.fetcher = new RangeBasedFetcher<Utc>(async (range) => {
      const generation = this.#staleGeneration;
      const view = this.#view;
      const compute = this.#authority.sheetComputes ? view?.compute ?? null : null;
      if (compute != null) {
        const naive = RowCellCache.#naiveRangeFromUtc(range);
        await compute(naive);
        if (generation !== this.#staleGeneration || this.#view !== view) return;
        this.isStale = false;
        this.#extendDeliveredRange(range);
        this.#rebuildAllIndices(naive);
        this.#fireListenersInRange(naive);
        return;
      }
      // A row a sheet holds locally: the holder's fetch wiring installs the
      // cells into the sheet's store (`CellAccessor`), decoded by its rows. A
      // canonical row's answer installs here, into the client's canonical
      // store, leaving a cell written locally while the read was out as it is.
      const canonical = this.#authority.installsOwnFetch ? this.#view : null;
      const ownedInstall = this.fetchInstallsAuthority;
      const since = ownedInstall ? null : canonical?.captureRevision() ?? null;
      let completed = false;
      let applied = false;
      const complete: RowFetchCompletion = (cells, install) => {
        if (completed) throw new Error("RowCellCache: response completed twice");
        // Latch before invoking user/store code: a failed commit never installs again.
        completed = true;
        const release = this.deferNotifications();
        let failed = false;
        try {
          // The captured holder's install precedes generation admission, as before.
          install();
          if (generation !== this.#staleGeneration) return;
          if (this.#authority.shadowsReads) this.#fetched.applyBatch(this.decodeCells(cells));
          this.#derived = null;
          this.isStale = false;
          this.#extendDeliveredRange(range);
          this.#notify(null);
          applied = true;
        } catch (error) {
          failed = true;
          throw error;
        } finally {
          try { release(); }
          catch (error) { if (!failed) throw error; }
        }
      };
      let failed = false;
      try {
        const cells = (await this.fetchFn(range, complete)) ?? [];
        if (!completed) {
          // Older producers return wire cells; their cache owns the same commit.
          complete(cells, () => {
            if (generation !== this.#staleGeneration) return;
            if (canonical != null && since != null && canonical === this.#view && cells.length > 0) {
              canonical.install(this.decodeCells(cells), since);
            }
          });
        }
      } catch (error) {
        failed = true;
        throw error;
      } finally {
        try {
          if (canonical != null && since != null) canonical.releaseRevision(since);
        } catch (error) { if (!failed) throw error; }
      }
      if (!applied) return;
      const onApplied = this.onNextFetchApplied;
      if (onApplied) {
        this.onNextFetchApplied = null;
        onApplied();
      }
    });
  }

  /**
   * A fresh cache for a sheet's derived source. Metadata
   * and index factories carry over; values, listeners, coverage and computation
   * belong to the new context, so none of the client's retained facts are adopted.
   */
  fork(): RowCellCache {
    const cache = new RowCellCache(this.rowId, this.fetchBindings.own, this.registry, this.listenerErrors);
    cache.lookupKey = this.lookupKey;
    cache.attributes = this.attributes;
    cache.encoding = this.encoding;
    cache.#fetched.cardinality = this.#fetched.cardinality;
    for (const [key, factory] of this.#factories) cache.#factories.set(key, factory);
    for (const listener of this.#carriedRebuilt) {
      cache.#carriedRebuilt.add(listener);
      cache.#subscriptions.onRebuilt((change) => listener(cache, change));
    }
    return cache;
  }

  // ── Notification batching ───────────────────────────────────────────────────
  //
  // Every mutator ends the same way: drop the derived view, rebuild EVERY
  // index from ALL cells, fire listeners. That is the right shape for one
  // write and the wrong shape for a burst — a batch install mirrored through
  // the store→handle bridge re-enters here once per cell, so N cells cost N
  // full index rebuilds (O(N²)) and N listener fan-outs, which is exactly the
  // `row-handle/fire-listeners` witness tripping at 31/s on a weather row
  // receiving 84 batched cells. `deferNotifications` lets the bridge bracket a
  // burst: writes still land in `fetched` immediately (synchronous readers
  // see them), but the rebuild + fire collapse into ONE at release, scoped to
  // the union of the touched ranges.

  #deferDepth = 0;
  /** A server read changed the cell set and its rebuild is still owed (`#receive`). */
  #indicesStale = false;
  #deferred: Option<{ range: Option<NaiveDate.Range>; all: boolean }> =
    null;

  /**
   * Suspend index rebuilds + listener fires until the returned release runs.
   * Re-entrant: nested calls share one flush, performed by the outermost
   * release. A release is idempotent. While deferred, `index` reads still
   * see fresh indices (the getter rebuilds on demand), so a reader inside
   * the bracket is never handed stale data — only the redundant per-write
   * rebuilds and the per-write listener fan-out are elided.
   */
  deferNotifications(): () => void {
    this.#deferDepth++;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.#deferDepth--;
      if (this.#deferDepth > 0) return;
      const pending = this.#deferred;
      this.#deferred = null;
      if (pending == null) return;
      const range = pending.all ? null : pending.range;
      this.#rebuildAllIndices(range);
      if (range == null) this.#fireListeners();
      else this.#fireListenersInRange(range);
    };
  }

  /** Rebuild indices + fire — now, or once at the end of the current
   *  `deferNotifications` bracket. `range == null` means every listener. */
  #notify(range: Option<NaiveDate.Range>): void {
    if (this.#deferDepth === 0) {
      this.#rebuildAllIndices(range);
      if (range == null) this.#fireListeners();
      else this.#fireListenersInRange(range);
      return;
    }
    const pending = this.#deferred ?? { range: null, all: false };
    if (range == null) {
      pending.all = true;
      pending.range = null;
    } else if (!pending.all) {
      const prev = pending.range;
      pending.range = prev == null ? range : new NaiveDate.Range(
        prev.start.dse <= range.start.dse ? prev.start : range.start,
        prev.end.dse >= range.end.dse ? prev.end : range.end,
      );
    }
    this.#deferred = pending;
  }

  // ── The one store (`#view`) ─────────────────────────────────────────────────

  /** The store view a holding sheet installed (`null`: the row's own cells). */
  get view(): Option<HeldCellStore> {
    return this.#view;
  }

  /**
   * @internal Install the store view of the sheet that holds the row
   * (`RowFetchBindings`); `null` when no sheet holds it any more: the row
   * reads its own cells again, marked stale so its next read fetches.
   */
  setView(view: Option<HeldCellStore>): void {
    if (this.#view === view) return;
    const had = this.#view != null;
    this.#view = view;
    this.#authority = view == null ? this.#ownAuthority : CellAuthority.held(view, this.#writeHost());
    this.#viewActors.clear();
    if (view == null && had) this.#beginStaleGeneration();
    // A row that held cells itself before a sheet held it: they join the store.
    if (view != null && this.#fetched.size > 0) {
      const release = this.deferNotifications();
      try {
        view.adopt(this.#fetched.getAll(), this.#fetched.getAllCustom());
      } finally {
        release();
      }
    }
    this.#notify(null);
  }

  /**
   * The store changed a cell of this row (`CellStoreView.link`). While the
   * store is this row's one store (`#view`): stamp the change's actor (what
   * `lastActorFor` answers, so a self-echo is recognised), record a local
   * write against snapshots still parked, take the cell's days as covered and
   * fire the listeners of its range — once per store burst. A row no sheet
   * holds keeps its own cells: the change is copied in (a server read's
   * ingest is not — that read installed here already).
   */
  storeChanged(change: StoreChange): void {
    this.#heard++;
    const view = this.#view;
    if (view == null) {
      this.#mirrorChange(change);
      return;
    }
    const local = !change.install && !change.server;
    if (change.kind === "date" && change.date != null) {
      const identities = [`${change.date.type}:${change.date.index}`];
      if (change.id != null && this.#fetched.cardinality === "multi") identities.push("id:" + change.id);
      for (const identity of identities) {
        if (change.actor != null) this.#viewActors.set(identity, change.actor);
        else this.#viewActors.delete(identity);
      }
      if (local) this.#recordLocalWrite(this.#identityOf(change.date, change.id));
      this.#markRealRange(change.range);
      this.#notify(change.range);
      return;
    }
    if (change.id != null) {
      const identity = "id:" + change.id;
      if (change.actor != null) this.#viewActors.set(identity, change.actor);
      else this.#viewActors.delete(identity);
      if (local) this.#recordLocalWrite(identity);
    }
    if (change.dtr != null) this.#extendFetchedRange(change.dtr);
    this.#notify(change.value == null ? change.range : null);
  }

  /** `storeChanged` for a row no sheet holds: copy the change into the row's own cells. */
  #mirrorChange(change: StoreChange): void {
    if (change.server) return;
    const actor = change.actor ?? STORE_ACTOR;
    if (change.kind === "date" && change.date != null) {
      if (change.install) this.#applyCellWrite(change.date, change.value, actor, change.id);
      else this.applyCellWriteLocal(change.date, change.value, actor, change.id);
      return;
    }
    if (change.id == null || change.dtr == null) return;
    const id = CellId.of(change.id);
    if (change.value == null) {
      this.#clearCustomCell(id, change.dtr, !change.install);
    } else {
      this.#replaceCustomCellWith(null, change.dtr, id, change.value, !change.install);
    }
  }

  /**
   * Run a write of the viewed store; when the store's change feed did not
   * reach this cache (no link resolves the row), notify `range` here.
   */
  #viaStore(range: Option<NaiveDate.Range>, write: () => void): void {
    const before = this.#heard;
    write();
    if (this.#heard === before) this.#notify(range);
  }

  // ── Index factory registration ──────────────────────────────────────────────

  get index(): Indices {
    // A deferred bracket may hold a pending rebuild; a reader inside it
    // still gets indices that reflect every write so far.
    if (this.#deferred != null || this.#indicesStale) this.#rebuildAllIndices();
    return this.#indices as Indices;
  }

  /**
   * Call `listener(handle, change)` now (`change.range` null: every day) and
   * after every change of this row's cell set, until the returned function
   * runs. `change.range` is the days the change touched (`null`: any day),
   * and the cells are read on demand (`cells()` / `cellsIn(range)`): a
   * listener that updates the days in `range` costs O(changed), never a
   * whole-row read (a listener that wants every cell reads `change.cells()`).
   * A throw at registration propagates and registers nothing; a later throw
   * is reported out of band like a subscriber's, never into the write that
   * rebuilt.
   */
  onCellsChanged(listener: (handle: RowCellCache, change: RowCellsChange) => void): Unsubscribe {
    listener(this, this.#cellsChange(null));
    return this.#subscriptions.onRebuilt((change) => listener(this, change));
  }

  /**
   * `onCellsChanged`, carried to forks the way an index factory is: a
   * `fork()` of this cache (made while the listener is registered) calls it
   * on the fork's own changes too, with the fork as `handle` (what an index
   * factory gets). Not called at fork time, as factories are not.
   */
  onCellsChangedCarried(listener: (handle: RowCellCache, change: RowCellsChange) => void): Unsubscribe {
    const off = this.onCellsChanged(listener);
    this.#carriedRebuilt.add(listener);
    return () => {
      off();
      this.#carriedRebuilt.delete(listener);
    };
  }

  /** The change a rebuild listener hears for `range`: the row's cells read on demand, the whole set at most once. */
  #cellsChange(range: Option<NaiveDate.Range>): RowCellsChange {
    const authority = this.#authority;
    let all: Option<CellEntry[]> = null;
    return {
      range,
      cells: (): CellEntry[] => all ??= authority.indexCells(),
      cellsIn: (within) => authority.indexCellsIn(within),
    };
  }

  withIndex<K extends string, O>(
    key: K,
    factory: IndexFactory<O>,
  ): RowCellCache<Indices & { readonly [P in K]: O }> {
    this.#factories.set(key, factory as IndexFactory<unknown>);
    this.#rebuildIndex(key);
    this.#assertIndexed<K, O>(key);
    return this;
  }

  /**
   * The handle holds an index under `key` (its factory is registered), so
   * its phantom `Indices` map includes `K` — the check `withIndex` narrows
   * `this` through.
   */
  #assertIndexed<K extends string, O>(
    key: K,
  ): asserts this is RowCellCache<Indices & { readonly [P in K]: O }> {
    if (!this.#factories.has(key)) {
      throw new Error(`RowCellCache.withIndex: no index registered under "${key}"`);
    }
  }

  // ── Staleness ────────────────────────────────────────────────────────────────

  /**
   * Mark stale and fire listeners. Subscribers re-evaluate immediately
   * against the current cells (which may be empty or stale). Use this
   * when the caller is NOT going to refresh — staleness is a deferred
   * signal for the next read. For atomic recompute-and-notify, use
   * `refresh()` instead.
   */
  markStale(): void {
    this.#beginStaleGeneration();
    if (this.#refreshActiveFormulaRanges()) return;
    this.#fireListeners();
  }

  /**
   * Atomic stale-then-fresh: marks stale, recomputes (formula rows) or
   * refetches (server-backed rows), and fires listeners once with fresh
   * data. Use this when you want a refresh without subscribers seeing a
   * stale/loading flash between the invalidation and the new data.
   */
  async refresh(range: NaiveDate.Range): Promise<void> {
    this.#beginStaleGeneration();
    await this.ensureFresh(range);
  }

  /**
   * Mark cell data as stale and reset both per-row and sheet-level fetch
   * caches so the next access triggers a full refetch (including sync
   * state from the bulk endpoint). Existing cached cells are kept so the
   * UI can show stale data while the refetch is in flight. Used when a
   * data-affecting attribute changes (e.g. weather location).
   */
  invalidateData(): void {
    this.#fetched.clear();
    this.#derived = null;
    this.#beginStaleGeneration();
    this.invalidateSheetCache?.();
    this.#rebuildAllIndices();
    if (this.#refreshActiveFormulaRanges()) return;
    this.#fireListeners();
  }

  // ── Async refresh ────────────────────────────────────────────────────────────

  #beginStaleGeneration(): void {
    this.#staleGeneration++;
    this.isStale = true;
    // Retire the old request immediately, even if no new reader has arrived
    // yet. Its eventual completion must neither install nor claim coverage.
    this.#resetStaleCoverage();
  }

  #resetStaleCoverage(): void {
    if (this.#resetStaleGeneration === this.#staleGeneration) return;
    this.#resetStaleGeneration = this.#staleGeneration;
    this.fetcher.fetchedRange = null;
    this.#vacuousCoverage = null;
    this.deliveredRange = null;
  }

  async ensureFresh(range: NaiveDate.Range): Promise<void> {
    requireNaiveDateRange("RowCellCache.ensureFresh", "range", range);
    const compute = this.#view?.compute ?? null;
    if (this.isStale && this.computeRange && compute != null) {
      // A computed row a sheet holds: its cells are the store's, computed by
      // the sheet (one engine) — load them there, take the range as covered.
      const generation = this.#staleGeneration;
      await compute(range);
      if (generation !== this.#staleGeneration) return;
      this.isStale = false;
      this.#extendFetchedRange(range.withTime().withTz(Utc));
      this.#notify(range);
      return;
    }
    if (this.isStale && this.computeRange) {
      const generation = this.#staleGeneration;
      const entries = await this.computeRange(range);
      if (generation !== this.#staleGeneration) return;
      this.#derived ??= new RowData();
      this.#derived.cardinality = this.#fetched.cardinality;
      this.#derived.replaceEntriesInRange(range, entries);
      this.isStale = false;
      this.#extendFetchedRange(range.withTime().withTz(Utc));
      this.#rebuildAllIndices();
      this.#fireListenersInRange(range);
      return;
    }
    if (this.isStale) {
      this.#resetStaleCoverage();
    }
    await this.fetcher.fetchRange(range.withTime().withTz(Utc));
  }

  async getCells(range: NaiveDate.Range): Promise<CellEntry[]> {
    requireNaiveDateRange("RowCellCache.getCells", "range", range);
    await this.ensureFresh(range);
    return this.getAllInRange(range);
  }

  // ── Sync reads ───────────────────────────────────────────────────────────────

  /**
   * Every cell this row holds in `range` — date-keyed and timepoint cells in
   * ONE list, each tagged with its `kind`. The single read door: a caller never
   * picks a store, so it cannot read one lane and miss a cell that routed to
   * the other (principle P1).
   *
   * Date-keyed cells match `range` directly; timepoint cells match its UTC day
   * bounds (`range.withTime().withTz(Utc)`), the same window `ensureFresh`
   * fetches. Series rows hold nothing of their own (their values live on
   * `sourceHandle`), so they return `[]`.
   */
  cellsIn(range: NaiveDate.Range): RowCellCache.Cell[] {
    requireNaiveDateRange("RowCellCache.cellsIn", "range", range);
    if (this.kind === "series") return [];
    return RowCellCache.#tagged(
      this.#authority.dates(range),
      this.#authority.timepoints(range.withTime().withTz(Utc)),
    );
  }

  /**
   * Every cell this row holds, unbounded — the same tagged list as `cellsIn`
   * for callers that genuinely scan the whole row (index rebuilds, probes).
   */
  allCells(): RowCellCache.Cell[] {
    if (this.kind === "series") return [];
    return RowCellCache.#tagged(
      this.#authority.dates(null),
      this.#authority.timepoints(null),
    );
  }

  static #tagged(
    dated: readonly CellEntry[],
    custom: readonly TimepointCellEntry[],
  ): RowCellCache.Cell[] {
    const out: RowCellCache.Cell[] = [];
    for (const entry of dated) out.push({ ...entry, kind: "date" });
    for (const entry of custom) out.push({ ...entry, kind: "timepoint" });
    return out;
  }

  getAllInRange(range: NaiveDate.Range): CellEntry[] {
    requireNaiveDateRange("RowCellCache.getAllInRange", "range", range);
    if (this.kind === "series") {
      return this.sourceHandle?.getAllInRange(range) ?? [];
    }
    return this.#authority.dates(range);
  }

  /**
   * The timepoint cell `id` as this row holds it now — its current range and
   * value, a move's optimistic range included — or `null` when the row holds
   * no timepoint cell with that id. Series rows read their source's.
   */
  peekTimepointCell(id: CellId): Option<TimepointCellEntry> {
    if (this.kind === "series") return this.sourceHandle?.peekTimepointCell(id) ?? null;
    return this.#authority.timepoints(null).find((cell) => cell.id === id) ?? null;
  }

  getAllTimepointsInRange(range: DateTime.Range<Utc>): TimepointCellEntry[] {
    requireDateTimeRange("RowCellCache.getAllTimepointsInRange", "range", range);
    if (this.kind === "series") {
      return this.sourceHandle?.getAllTimepointsInRange(range) ?? [];
    }
    return this.#authority.timepoints(range);
  }

  /**
   * Returns the actor that most recently stamped this cell via a local
   * write (see `applyCellWriteLocal`). Sync-driven updates do not stamp
   * an actor, so this returns `null` for cells whose latest value came
   * from the server. Used by `subscribe.evaluate` to surface
   * `change.actor` on the `CellHandle.onData` callback. Delegates via
   * `sourceHandle` for series rows (renderer-virtual projections that
   * transparently read from their source row).
   */
  lastActorFor(column: NaiveDate.Partial, id: Option<string>): Option<Actor> {
    if (this.kind === "series") {
      return this.sourceHandle?.lastActorFor(column, id) ?? null;
    }
    if (this.#view != null) {
      const identity = id != null && this.#fetched.cardinality === "multi" ? "id:" + id : `${column.type}:${column.index}`;
      return this.#viewActors.get(identity) ?? null;
    }
    return (this.#derived ?? this.#fetched).lastActorFor(column, id);
  }

  // ── Fetch ────────────────────────────────────────────────────────────────────

  fetch(range: DateTime.Range<Utc>): Promise<Option<DateTime.Range<Utc>>> {
    requireDateTimeRange("RowCellCache.fetch", "range", range);
    if (this.kind === "series" && this.sourceHandle != null) {
      return this.sourceHandle.fetch(range);
    }
    return this.fetcher.fetchRange(range).then((r) => r ?? null);
  }

  // ── Internal update path ────────────────────────────────────────────────────

  /**
   * @internal Union of ranges actually DELIVERED by a server fetch
   * (`receiveBatchData` or this handle's own fetcher round-trip).
   * Distinct from `fetcher.fetchedRange`, which is extended
   * optimistically at fetch START (`RangeBasedFetcher.createFetchEntry`)
   * — so mid-fetch it cannot answer "has the server told us about
   * this range?". Read by the accessor's per-row fetch fallback to
   * decide whether the bulk sheet fetch already covered a row.
   */
  deliveredRange: Option<DateTime.Range<Utc>> = null;

  /**
   * Union of ranges a sheet-level bulk fetch covered WITHOUT this row
   * appearing in the response ("vacuously covered": the server saw the
   * range and had nothing for this row). Kept OUT of
   * `fetcher.fetchedRange` on purpose: subscribers need it to settle
   * placeholder → dataFull(null), but it must never dedup a real
   * fetch — rows with per-row fallback sources (lookup-keyed rows in
   * ephemeral sheets) still have data the bulk path can't see.
   */
  #vacuousCoverage: Option<DateTime.Range<Utc>> = null;

  /** True when `dt` falls inside real fetch coverage OR vacuous bulk
   *  coverage — the subscriber-facing "do we know about this date?". */
  isCoveredAt(dt: DateTime<Utc>): boolean {
    if (this.kind === "series") {
      return this.sourceHandle?.isCoveredAt(dt) ?? false;
    }
    if (this.computeRange) {
      return this.fetcher.fetchedRange?.containsExclusiveEnd(dt) ?? false;
    }
    if (this.fetcher.fetchedRange?.containsExclusiveEnd(dt)) return true;
    return this.#vacuousCoverage?.containsExclusiveEnd(dt) ?? false;
  }

  /** True when any range — fetched or vacuously covered — is known. */
  get hasCoverage(): boolean {
    if (this.kind === "series") {
      return this.sourceHandle?.hasCoverage ?? false;
    }
    return this.fetcher.fetchedRange != null || this.#vacuousCoverage != null;
  }

  /**
   * True while a fetch is in flight and NO server response has settled
   * for this row (nothing delivered, no vacuous bulk coverage).
   * `hasCoverage` alone cannot answer "should a subscriber shimmer?":
   * `fetcher.fetchedRange` is claimed OPTIMISTICALLY at fetch start, so
   * mid-flight it reports coverage for cells the server has not answered
   * for. The weather-relocation regression class: the re-keyed row's
   * first bind starts the canonical-backstop fetch, and every other
   * subscriber then saw `hasCoverage=true` + zero cells → `onData([])`
   * (blank/stale paint, loading=false) instead of `onLoading` (shimmer)
   * for the whole fetch window. Rows whose only coverage is non-server
   * (vacuous `markRangeFetched`, local ephemeral writes, computed
   * ranges) run no fetch, so this stays false and their covered-empty
   * rendering is preserved (no perpetual shimmer —
   * .learnings/2026-06-19-vacuous-coverage-flash-lookupkey.toml).
   */
  get isFetchPending(): boolean {
    if (this.kind === "series") {
      return this.sourceHandle?.isFetchPending ?? false;
    }
    return (
      this.fetcher.hasOngoingFetches &&
      this.deliveredRange == null &&
      this.#vacuousCoverage == null
    );
  }

  /**
   * Local-write revisions (project sheet-scroll-smoothness, candidate D): a
   * server snapshot that waited on the ingestion gate must not overwrite a
   * cell edited after it arrived. Every optimistic, external or mirrored
   * *local* edit bumps `writeRevision` and stamps the cell's storage identity
   * (a multi-cardinality or custom cell by id, a single-cardinality cell by
   * day — what `RowData` keys on); a mirrored *install* does not.
   * `receiveBatchDataSince` skips cells stamped after the revision captured
   * when the response landed. Stamps are bounded by outstanding captures —
   * see `captureWriteRevision` / `releaseWriteRevision`.
   */
  #writeRev = 0;
  readonly #localWrites = new Map<string, number>();
  readonly #outstanding = new Map<number, number>();

  get writeRevision(): number {
    return this.#writeRev;
  }

  captureWriteRevision(): number {
    const rev = this.#writeRev;
    this.#outstanding.set(rev, (this.#outstanding.get(rev) ?? 0) + 1);
    return rev;
  }

  releaseWriteRevision(rev: number): void {
    const n = this.#outstanding.get(rev);
    if (n == null) return;
    if (n > 1) this.#outstanding.set(rev, n - 1);
    else this.#outstanding.delete(rev);
    if (this.#outstanding.size === 0) {
      this.#localWrites.clear();
      return;
    }
    let min = Infinity;
    for (const r of this.#outstanding.keys()) if (r < min) min = r;
    for (const [identity, r] of this.#localWrites) if (r <= min) this.#localWrites.delete(identity);
  }

  /** @internal Test hook: stamps currently retained. */
  localWriteCount(): number {
    return this.#localWrites.size;
  }

  /**
   * The cache's side of every write (`CellWriteHost`): its local-write
   * ledger, coverage and notification, handed to both write sides.
   */
  #writeHost(): CellWriteHost {
    return {
      identityOf: (date, id) => this.#identityOf(date, id),
      recordLocalWrite: (identity) => this.#recordLocalWrite(identity),
      dropDerived: () => {
        this.#derived = null;
      },
      markRealRange: (range) => this.#markRealRange(range),
      extendFetchedRange: (range) => {
        this.#extendFetchedRange(range);
      },
      notify: (range) => this.#notify(range),
      rebuildAndFire: (range) => {
        this.#rebuildAllIndices(range);
        if (range == null) this.#fireListeners();
        else this.#fireListenersInRange(range);
      },
      viaStore: (range, write) => this.#viaStore(range, write),
      deferred: (fn) => {
        const release = this.deferNotifications();
        try {
          fn();
        } finally {
          release();
        }
      },
      cardinality: () => this.#fetched.cardinality,
    };
  }

  #identityOf(date: NaiveDate.Partial, id: Option<string>): string {
    return id != null && this.#fetched.cardinality === "multi" ? "id:" + id : "date:" + date.toString();
  }

  #recordLocalWrite(identity: string): void {
    this.#writeRev++;
    if (this.#outstanding.size === 0) return;
    this.#localWrites.set(identity, this.#writeRev);
  }

  #writtenSince(since: number, cell: Network.SheetCell): boolean {
    const day = SemanticTime.partialDate(cell.date);
    const identity = day == null
      ? (cell.id != null ? "id:" + cell.id : null)
      : this.#identityOf(day, cell.id ?? null);
    if (identity == null) return false;
    const r = this.#localWrites.get(identity);
    return r != null && r > since;
  }

  /**
   * `receiveBatchData` for a snapshot that waited: cells written locally after
   * `since` are left as they are, everything else installs as before.
   */
  receiveBatchDataSince(
    range: Option<DateTime.Range<Utc>>,
    cells: Network.SheetCell[],
    since: number,
  ): RowCellCache.Receipt {
    // The sheet's own ingest installs these cells into a sheet store (the
    // accessor's bulk observer); only the canonical store takes them here.
    return this.#receive(range, this.filterBatchDataSince(cells, since), this.#authority.installsOwnFetch);
  }

  /** SDK bulk observer owns installation; this updates coverage under the same listener bracket. */
  receiveBatchCoverageSince(range: DateTime.Range<Utc>, cells: Network.SheetCell[], since: number): RowCellCache.Receipt {
    return this.#receive(range, this.filterBatchDataSince(cells, since), false);
  }

  /** Keep a parked snapshot's local-write protection across observer delivery. */
  filterBatchDataSince(cells: Network.SheetCell[], since: number): Network.SheetCell[] {
    return since < this.#writeRev
      ? cells.filter((cell) => !this.#writtenSince(since, cell))
      : cells;
  }

  /**
   * A server read of this row's cells over `range` (`null`: no range): its
   * cells install as a snapshot — into the one store while a sheet holds the
   * row — and `range` counts as delivered and covered. Answers whether the
   * row's own copy changed (either `Receipt`).
   */
  receiveBatchData(
    range: Option<DateTime.Range<Utc>>,
    cells: Network.SheetCell[],
  ): boolean {
    return this.#receive(range, cells, true) != null;
  }

  #receive(
    range: Option<DateTime.Range<Utc>>,
    cells: Network.SheetCell[],
    install: boolean,
  ): RowCellCache.Receipt {
    if (range != null) {
      requireDateTimeRange("RowCellCache.receiveBatchData", "range", range);
    }
    if (this.kind === "series") return null;
    // One rebuild per read: the cell-set change is marked here and rebuilt once,
    // by the notification when the read changed anything, else at the end
    // (`index` reads in between rebuild on demand).
    const release = this.deferNotifications();
    try {
      return this.#receiveDeferred(range, cells, install);
    } finally {
      release();
      if (this.#indicesStale) this.#rebuildAllIndices();
    }
  }

  #receiveDeferred(
    range: Option<DateTime.Range<Utc>>,
    cells: Network.SheetCell[],
    install: boolean,
  ): RowCellCache.Receipt {
    // Under a sheet-held view the read also lands in the row's own cells (`CellAuthority`): their change answers
    // this call. One decode (one count) serves them and the holder's install; each store keeps its own entries.
    let decoded: Option<RowData.Cell[]> = null;
    const changed = this.#authority.shadowsReads ? this.#fetched.applyBatchWithChange(decoded = this.decodeCells(cells)) : false;
    const hasComputedCells = this.computeRange != null;
    // Computed rows are sheet-scoped derived data. Bulk server fetches may
    // deliver metadata for them, but must not erase derived cells or claim
    // compute coverage; only ensureFresh/refresh owns compute coverage.
    if (!hasComputedCells) {
      this.#derived = null;
      this.isStale = false;
    }
    this.#indicesStale = true;
    let expanded = false;
    if (range != null) {
      this.#vacuousCoverage = null;
      this.#extendDeliveredRange(range);
      if (!hasComputedCells) expanded = this.#extendFetchedRange(range);
    }
    if (install) this.#authority.writes.installRead(() => decoded ?? this.decodeCells(cells));
    if (changed || expanded) {
      this.#notify(range != null ? RowCellCache.#naiveRangeFromUtc(range) : null);
    }
    if (changed) return "changed";
    // A client-owned store already holds the facts (it announced each cell it
    // changed); first delivered coverage is still the holder's news: a change
    // when the read carried cells, a bare coverage when it carried none.
    if (this.#authority.clientOwned && expanded) return cells.length > 0 ? "changed" : "covered";
    return null;
  }

  #extendDeliveredRange(range: DateTime.Range<Utc>): void {
    const dr = this.deliveredRange;
    this.deliveredRange = dr
      ? new DateTime.Range(
        range.start.mse < dr.start.mse ? range.start : dr.start,
        range.end.mse > dr.end.mse ? range.end : dr.end,
      )
      : range;
  }

  /** Replace all custom cells in the given range with the provided cells. */
  replaceCustomCells(
    range: DateTime.Range<Utc>,
    cells: readonly RowData.Cell[],
  ): void {
    requireDateTimeRange("RowCellCache.replaceCustomCells", "range", range);
    this.#authority.writes.replaceTimepoints(range, cells);
  }

  /** Apply a custom-cell write response without clearing range neighbors;
   *  the answer's cells are stamped with the writer's `actor` (`null`: an
   *  answer no local writer made, read back as a remote change). */
  applyCustomWriteResponse(
    removeId: Option<CellId>,
    cells: Network.SheetCell[],
    actor: Option<Actor>,
  ): void {
    if (removeId != null) {
      requireNonEmptyKey(
        "RowCellCache.applyCustomWriteResponse",
        "removeId",
        removeId,
      );
    }
    this.#authority.writes.writeAnswer(removeId, cells, actor);
  }

  /**
   * Synchronous optimistic custom-cell move: remove the old cell by id and
   * upsert the moved cell at the new range, so a drag/resize settles before
   * the network round-trip (the custom-cell analog of the events
   * `canonicalCells` write). `setCustom`'s success path then reconciles to
   * server truth by id, so neighboring custom cells in the old range survive.
   * `id` MUST be the raw cell id — the span reader keys on it.
   */
  optimisticMoveTimepointCell(
    oldRange: DateTime.Range<Utc>,
    newRange: DateTime.Range<Utc>,
    id: CellId,
    data: Json | CellValue,
  ): void {
    requireDateTimeRange(
      "RowCellCache.optimisticMoveTimepointCell",
      "oldRange",
      oldRange,
    );
    requireDateTimeRange(
      "RowCellCache.optimisticMoveTimepointCell",
      "newRange",
      newRange,
    );
    requireNonEmptyKey("RowCellCache.optimisticMoveTimepointCell", "id", id);
    this.#authority.writes.moveTimepoint(newRange, id, data);
  }

  /**
   * Custom-cell analog of `applyExternalEdit`: upsert ONE timepoint
   * cell into the store. Used by in-memory backends (ephemeral rows
   * persist cells here, not server-side) and other store-direct echo
   * paths.
   */
  applyExternalCustomEdit(
    dtr: DateTime.Range<Utc>,
    id: CellId,
    data: Json | CellValue,
  ): void {
    requireDateTimeRange("RowCellCache.applyExternalCustomEdit", "dtr", dtr);
    requireNonEmptyKey("RowCellCache.applyExternalCustomEdit", "id", id);
    this.replaceCustomCell(null, dtr, id, data);
  }

  /** Clear one custom cell mirrored from a local canonical-store write. */
  applyExternalCustomClear(id: CellId, range: DateTime.Range<Utc>): void {
    this.#clearCustomCell(id, range, true);
  }

  #clearCustomCell(id: CellId, range: DateTime.Range<Utc>, stamp: boolean): void {
    requireNonEmptyKey("RowCellCache.clearCustomCell", "id", id);
    requireDateTimeRange("RowCellCache.clearCustomCell", "range", range);
    this.#authority.writes.clearTimepoint(id, range, stamp);
  }

  /**
   * Atomic in-memory custom-cell replace: optionally remove one cell
   * by raw id (the server's `replaceCellId` move semantics — the old
   * cell may live in a different range), then upsert the new cell —
   * ONE index rebuild + ONE listener fire for the whole operation.
   */
  replaceCustomCell(
    removeId: Option<CellId>,
    dtr: DateTime.Range<Utc>,
    id: CellId,
    data: Json | CellValue,
  ): void {
    this.#replaceCustomCellWith(removeId, dtr, id, data, true);
  }

  #replaceCustomCellWith(
    removeId: Option<CellId>,
    dtr: DateTime.Range<Utc>,
    id: CellId,
    data: Json | CellValue,
    stamp: boolean,
  ): void {
    if (removeId != null) {
      requireNonEmptyKey("RowCellCache.replaceCustomCell", "removeId", removeId);
    }
    requireDateTimeRange("RowCellCache.replaceCustomCell", "dtr", dtr);
    requireNonEmptyKey("RowCellCache.replaceCustomCell", "id", id);
    this.#authority.writes.setTimepoint(removeId, dtr, id, data, stamp);
  }

  /** Target address for a `(id, date)` write — by cardinality. See
   *  `RowData.addressOf`. */
  addressOf(id: Option<CellId>, date: NaiveDate.Partial): CellAddress {
    return this.#fetched.addressOf(id, date);
  }

  /** Resolve address — where the cell for `id` currently lives (custom by id,
   *  else the standard cell at `date` if it holds this id, else null). The one
   *  "which store?" authority; see `RowData.addressOfExisting`. Probes the same
   *  `fetched` store the custom-cell mutators touch. */
  addressOfExisting(id: CellId, date: NaiveDate.Partial): Option<CellAddress> {
    requireNonEmptyKey("RowCellCache.addressOfExisting", "id", id);
    return this.#authority.writes.addressOfExisting(id, date);
  }

  /**
   * Remove the cell at a resolved {@link CellAddress} — the single
   * address-based removal mutator (pair with `addressOfExisting`). `custom`
   * removes one cell by raw id (any range; neighbors untouched) and returns
   * its range; `standard` clears the date-keyed cell and returns null (the
   * standard store has no per-cell range). The caller no longer branches on
   * the store: it resolves the address, then removes at it.
   */
  removeCell(addr: CellAddress): Option<DateTime.Range<Utc>> {
    return this.#authority.writes.remove(addr);
  }

  applyExternalEdit(
    date: NaiveDate.Partial,
    data: Json | CellValue,
    id: Option<string>,
  ): void {
    this.#authority.writes.setDate(date, data, id ?? null, null);
  }

  /**
   * Mirror a locally-originated cell write into this handle's cache,
   * stamped with the originator's actor. Called by the source-store →
   * `fetched` bridge (see `sheet-row.ts`) so the next `CellHandle.onData`
   * fire surfaces `change.actor` and plugins can opt out of self-echo
   * rebinds. Same propagation as `applyExternalEdit` (rebuild indices,
   * replace vacuous coverage with real coverage, fire listeners).
   */
  applyCellWriteLocal(
    date: NaiveDate.Partial,
    data: LocalCellValue,
    actor: Actor,
    id: Option<string>,
  ): void {
    this.#authority.writes.setDate(date, data, id, actor);
  }

  /** A local write copied into the row's own cells (a store change of a row no sheet holds). */
  #applyCellWrite(
    date: NaiveDate.Partial,
    data: LocalCellValue,
    actor: Actor,
    id: Option<string>,
  ): void {
    const range = date.toRange();
    this.#fetched.applyLocalWrite(date, data, actor, id);
    this.#derived = null;
    this.#markRealRange(range);
    this.#notify(range);
  }

  /**
   * Batch-insert multiple cells and rebuild indices once instead of per-cell.
   * Used by syncFromParent to avoid O(N²) index rebuilds.
   */
  batchExternalEdits(
    edits: readonly { readonly date: NaiveDate.Partial; readonly data: Json | CellValue }[],
  ): void {
    this.#authority.writes.setDates(edits);
  }

  /** @internal Mark a range as fetched (with no new data) and notify subscribers. */
  markRangeFetched(range: DateTime.Range<Utc>): void {
    const vc = this.#vacuousCoverage;
    this.#vacuousCoverage = vc
      ? new DateTime.Range(
        range.start.mse < vc.start.mse ? range.start : vc.start,
        range.end.mse > vc.end.mse ? range.end : vc.end,
      )
      : range;
    this.#fireListenersInRange(RowCellCache.#naiveRangeFromUtc(range));
  }

  #markRealRange(range: NaiveDate.Range): void {
    this.#vacuousCoverage = null;
    const utcStart = range.start.withTime().withUtcTz();
    const utcEnd = range.end.withTime().withUtcTz();
    this.#extendFetchedRange(new DateTime.Range(utcStart, utcEnd));
  }

  #extendFetchedRange(range: DateTime.Range<Utc>): boolean {
    return this.fetcher.noteInstalled(range);
  }

  // ── Subscriptions ────────────────────────────────────────────────────────────

  onChange(cb: () => void): Unsubscribe {
    return this.#subscriptions.onChange(cb);
  }

  readonly #droppedListeners = new Set<() => void>();

  /**
   * Run `listener` once when the backend drops this cache from its registry
   * (`dropped`): an idle row its retention evicted, or a deleted row. What an
   * owner attached to the cache's lifetime — the client's eviction guard on
   * its shared cell store, a closure over this cache — is released here;
   * kept, it held every dropped cache (and its subscriptions, authorities
   * and fetcher) reachable for the client's life. Returns the unsubscribe.
   */
  onDropped(listener: () => void): Unsubscribe {
    this.#droppedListeners.add(listener);
    return () => {
      this.#droppedListeners.delete(listener);
    };
  }

  /**
   * The backend dropped this cache (`onDropped`): each listener runs once,
   * all of them even when one throws (what they threw goes to
   * `listenerErrors`).
   */
  dropped(): void {
    const listeners = [...this.#droppedListeners];
    this.#droppedListeners.clear();
    const errors = ListenerErrors.fanOut(listeners, () => true, (listener) => listener());
    ListenerErrors.report(this.listenerErrors, errors, "RowCellCache.dropped");
  }

  /** Wire this handle as a `"series"` projection of `source`: cell reads
   *  (`getAllInRange`, `lastActorFor`) delegate to the source's `fetched`,
   *  and source changes reach active listeners on this row. With no listeners,
   *  the source remains readable but does not need a forwarding subscription. */
  bindSourceHandle(source: RowCellCache): void {
    this.#sourceChangeUnsub?.();
    this.#sourceChangeUnsub = null;
    this.sourceHandle = source;
    this.kind = "series";
    this.#reconcileSourceForwarding();
  }

  /** Listen to the source only while a projection subscriber needs a fire.
   *  A recycled renderer with no subscribers must not turn every source
   *  coverage or data notification into an empty fan-out. */
  #reconcileSourceForwarding(): void {
    const source = this.sourceHandle;
    if (this.kind !== "series" || source == null || !this.#subscriptions.hasListeners) {
      this.#sourceChangeUnsub?.();
      this.#sourceChangeUnsub = null;
    } else if (this.#sourceChangeUnsub == null) {
      this.#sourceChangeUnsub = source.onChange(() => this.#fireListeners());
    }
  }

  // ── Retention (the backend's idle-row LRU, `RowCacheRetention`) ─────────────

  /** Cells this row holds (date-keyed and timepoint, fetched and derived): what an idle row costs. */
  get cellCount(): number {
    return this.#authority.cellCount();
  }

  /**
   * Whether the backend may drop this row now: no open sheet holds it
   * (`fetchBindings.holders`), nobody listens (change and sync-state
   * listeners, and every live `subscribe` reader), no fetch is in flight, no
   * notification bracket is open and no write revision is outstanding —
   * nothing would notice the drop but the next read, which fetches again.
   */
  get isEvictable(): boolean {
    return this.fetchBindings.holders === 0 &&
      this.#subscriptions.isIdle &&
      !this.fetcher.hasOngoingFetches &&
      this.#deferDepth === 0 &&
      this.#outstanding.size === 0;
  }

  /**
   * The backend evicted this row (`RowCacheRetention`): drop its cells and its
   * fetch coverage, so a reader still holding this object reads nothing stale
   * — its next read fetches again — and retire any response still to land.
   */
  evictCells(): void {
    this.#fetched.clear();
    this.#derived = null;
    this.#localWrites.clear();
    this.#beginStaleGeneration();
    this.#sourceChangeUnsub?.();
    this.#sourceChangeUnsub = null;
    this.#rebuildAllIndices();
  }

  disposeAll(): void {
    this.#localWrites.clear();
    this.#outstanding.clear();
    this.#subscriptions.disposeAll();
    this.#sourceChangeUnsub?.();
    this.#sourceChangeUnsub = null;
  }

  // ── Sync state ───────────────────────────────────────────────────────────────

  setSyncState(state: Option<RowSyncState>): void {
    this.syncState = state;
    // Every listener hears the change even when an earlier one throws.
    this.#subscriptions.fireSyncState(state, this.onSyncStateChanged);
  }

  /**
   * Subscribe to sync-state changes; fires with the new state on every
   * `setSyncState`. Any number of listeners may subscribe. Returns the
   * unsubscribe function.
   */
  onSyncState(listener: (state: Option<RowSyncState>) => void): Unsubscribe {
    return this.#subscriptions.onSyncState(listener);
  }

  /** Resolves once no sync is in progress — or when `disposeAll` retires
   *  this row, after which no sync state will ever arrive. */
  waitForSync(): Promise<void> {
    if (!this.syncState) return Promise.resolve();
    return this.#subscriptions.waitUntil(() => !this.syncState);
  }

  subscribe(
    column: NaiveDate.Partial,
    callbacks: SubscribeCallbacks,
    options: Option<SubscribeOptions>,
  ): SubscribeHandle {
    const id = `${this.rowId}:${column.toString()}`;
    const range = column.toRange();
    const utcRange = range.withTime().withTz(Utc);

    // `id` names the column and is shared by every reader of it; the token is
    // this caller's own registration (`RowSubscriptions.acquire`).
    const subscriptions = this.#subscriptions;
    const token = subscriptions.acquire(id);
    const isValid = () => subscriptions.isLive(token);

    const evaluate = () => {
      if (!isValid()) return;
      const inRange = this.isCoveredAt(DateTime.fromDse(range.start.dse, Utc));
      const cells = this.getAllInRange(range);
      if (!inRange && cells.length === 0) {
        callbacks.onPlaceholderData();
        return;
      }
      const cell = cells[0] ?? null;
      const syncState = this.syncState ?? {
        syncKeys: new Map(),
        status: "complete" as const,
      };
      // Resolve the actor that most recently stamped this cell via a
      // local write (the bridge in `sheet-row.ts` mirrors source-store
      // writes into `applyCellWriteLocal` with the originator's actor).
      // Sync-driven updates clear the stamp in `applyBatch`, so server
      // roundtrips read back as `null` here — only locally-originated
      // writes participate in self-echo suppression. Delegation to
      // `sourceHandle` for series rows is handled inside `lastActorFor`.
      const actor = this.lastActorFor(column, null);
      const change: DataFireChange = { actor };
      callbacks.onDataWithStage(cell, syncState, change);
      if (!this.syncState) callbacks.onDataFull(cell, change);
    };

    // Acquisition is atomic: if the initial evaluation (caller callbacks) or
    // the change registration throws, the token and any listener already
    // registered are released before the error propagates, so a failed
    // `subscribe` leaves no live reader behind (`isEvictable`).
    let unsub: Option<Unsubscribe> = null;
    const release = (): void => {
      subscriptions.release(token);
      const registered = unsub;
      unsub = null;
      registered?.();
    };
    try {
      evaluate();
      // An initial callback may have retired every reader (`disposeAll`).
      if (isValid()) {
        const subscribeToChanges = options?.subscribeToChanges ?? null;
        if (subscribeToChanges != null) {
          unsub = subscribeToChanges(evaluate);
        } else {
          unsub = subscriptions.watchRange(token, range, evaluate);
        }
      }
    } catch (error) {
      try {
        release();
      } catch (cleanup) {
        Tracing.dev("row-handle/subscribe-rollback-error", {
          rowId: this.rowId.toString(),
          err: cleanup instanceof Error ? cleanup.message : String(cleanup),
        });
      }
      throw error;
    }

    const startFetch = () => this.#subscribeFetch(range, utcRange);
    return {
      id,
      dispose: release,
      fetch: () => {
        const started = startFetch();
        if (started == null) return;
        // Fire-and-forget — but NEVER bare: an un-`.catch`ed rejection here
        // escapes as an unhandled promise rejection (in Deno tests it fails
        // the whole module — the ":~lona:tasks" Invalid-URL cancellation;
        // see data-address/migration-plan.md Phase E).
        // Logged at warn, not dev: a refused, unanswered or
        // malformed read here is otherwise invisible — the subscriber keeps
        // its placeholder. A caller that must know uses `fetchChecked`
        // (`CellHandle.onError`).
        void started.work.catch((err) => {
          Tracing.warn("net")(started.label, {
            rowId: this.rowId.toString(),
            err: err instanceof Error ? err.message : String(err),
          });
        });
      },
      fetchChecked: async () => {
        const started = startFetch();
        if (started != null) await started.work;
      },
    };
  }

  /**
   * Re-evaluate a covered, fresh, empty `range` of a computed row without a
   * new stale generation: the row keeps its coverage, and listeners hear of
   * it only when the evaluation leaves a cell there. A row a sheet holds is
   * evaluated by the sheet (`#view.compute`, whose store writes notify through
   * the store's own change feed, and only on a change); a standalone computed
   * row by its own `computeRange`.
   */
  async #reevaluateEmpty(range: NaiveDate.Range): Promise<void> {
    const compute = this.#view?.compute ?? null;
    if (compute != null) {
      await compute(range);
      return;
    }
    const computeRange = this.computeRange;
    if (computeRange == null) return;
    const generation = this.#staleGeneration;
    const entries = await computeRange(range);
    if (generation !== this.#staleGeneration || entries.length === 0) return;
    this.#derived ??= new RowData();
    this.#derived.cardinality = this.#fetched.cardinality;
    this.#derived.replaceEntriesInRange(range, entries);
    this.#rebuildAllIndices();
    this.#fireListenersInRange(range);
  }

  /**
   * Compute a fresh row's uncovered `range` in place, when a sheet holds the
   * row (`#view.compute`): the sheet's engine writes the range's cells into
   * its store, whose change feed tells listeners only of what changed, and
   * the range joins the row's coverage. No new stale generation, so the
   * coverage the row already has stays. `false` (nothing done) for a row no
   * sheet holds; such a row takes `refresh`.
   *
   * `refresh` here dropped the row's whole coverage, leaving only `range`
   * covered, so the next bind outside it refreshed again: once a sheet-wide
   * recompute (`CellAccessor.#evaluateFormulas`) had narrowed a formula
   * row's coverage, every bind of a scroll's way back was a recompute and a
   * fan-out to every listener (dom-to-sdk-18: 31/s on the Sum row).
   */
  async #computeUncovered(range: NaiveDate.Range): Promise<boolean> {
    // The fetcher's two checks (its compute branch): the sheet computes this
    // row's cells (`sheetComputes`), and the view is still the one computed
    // through when the answer lands.
    const view = this.#view;
    const compute = this.#authority.sheetComputes ? view?.compute ?? null : null;
    if (compute == null) return false;
    const generation = this.#staleGeneration;
    await compute(range);
    if (generation === this.#staleGeneration && !this.isStale && this.#view === view) {
      this.#extendFetchedRange(range.withTime().withTz(Utc));
    }
    return true;
  }

  /**
   * `subscribe`'s fetch of `range`: the request that loads the column, or
   * `null` when the cache already covers it. `label` names a failure in the
   * dev trace (`SubscribeHandle.fetch`).
   */
  #subscribeFetch(
    range: NaiveDate.Range,
    utcRange: DateTime.Range<Utc>,
  ): Option<{ readonly work: Promise<void>; readonly label: string }> {
    if (this.computeRange) {
      const cached = this.isCoveredAt(
        DateTime.fromDse(range.start.dse, Utc),
      );
      // A covered, fresh day with no cell is re-evaluated for that day only,
      // in place (`#reevaluateEmpty`): it may have gained a value its inputs
      // did not announce, and a formula over a missing input has none (the
      // 0.1.0 rule). It used to `refresh` the whole row: a new stale
      // generation that also dropped the row's coverage, so every later bind
      // refreshed again, each a recompute and a fan-out to every listener
      // (dom-to-sdk-16: 31/s on a scrolled formula row whose input misses
      // every fifth day).
      if (!this.isStale && cached) {
        if (this.getAllInRange(range).length > 0) return null;
        return { work: this.#reevaluateEmpty(range), label: "row-handle/subscribe-refresh-error" };
      }
      const refresh = async () => {
        if (!cached && this.sheetFetch) {
          await this.sheetFetch(range);
          const nowCached = this.isCoveredAt(
            DateTime.fromDse(range.start.dse, Utc),
          );
          if (!this.isStale && nowCached) {
            if (this.getAllInRange(range).length === 0) await this.#reevaluateEmpty(range);
            return;
          }
        }
        if (!cached && !this.isStale && await this.#computeUncovered(range)) return;
        await this.refresh(range);
      };
      return { work: refresh(), label: "row-handle/subscribe-refresh-error" };
    }
    const sheetFetch = this.sheetFetch;
    if (sheetFetch) {
      const cached = this.isCoveredAt(
        DateTime.fromDse(range.start.dse, Utc),
      );
      if (!cached) {
        const fetchSheetThenSource = async () => {
          await sheetFetch(range);
          const nowCached = this.isCoveredAt(
            DateTime.fromDse(range.start.dse, Utc),
          );
          if (!nowCached) {
            await this.fetch(utcRange);
          }
        };
        return {
          work: fetchSheetThenSource(),
          label: "row-handle/subscribe-sheet-fetch-error",
        };
      }
      return null;
    }
    return {
      work: this.fetch(utcRange).then(() => {}),
      label: "row-handle/subscribe-fetch-error",
    };
  }

  // ── Private ──────────────────────────────────────────────────────────────────

  /**
   * Decode a write's answered cell value (POST/PATCH to the cells route)
   * through the row's server-declared `encoding` by the one read rule
   * (`EncodingRegistry.decodeRead`, as `decodeCells`): a wire `null` stays
   * `null`; a rejected value is `null` here (the caller keeps nothing),
   * counted against the row and traced. Without an encoding or a registry
   * the value passes through — callers can treat it as already canonical.
   */
  decodeCell(raw: Json): Option<Json | CellValue> {
    if (!this.registry || !this.encoding) return raw;
    const read = this.registry.readOf(this.encoding, this.rowId.toString());
    const value = read.decode(raw, "write answer");
    read.end();
    return value === EncodingRegistry.REJECTED ? null : value;
  }

  /**
   * Cell hydration seam: the wire cells as the store holds them. Consults
   * the encoding registry (if the backend wired one) and decodes each
   * `cell.data` through the row's server-declared `encoding` into a new
   * store cell (`RowData.Cell`, whose `data` is the canonical value), by the
   * one read rule (`EncodingRegistry.decodeRead`, as a row's own read
   * decodes): a wire `null` stays a null cell, a cell the decoder rejects is
   * left out (counted and traced) — indices + variants never see wire-shape
   * or invalid cells. The wire cells are not touched. Without an encoding
   * or a registry the cells pass through as they are.
   */
  decodeCells(cells: readonly Network.SheetCell[]): RowData.Cell[] {
    const registry = this.registry;
    const enc = this.encoding;
    if (!registry || !enc) return [...cells];
    const read = registry.readOf(enc, this.rowId.toString());
    const out: RowData.Cell[] = [];
    for (const cell of cells) {
      const data = read.decode(cell.data, cell.id ?? cell.date);
      if (data === EncodingRegistry.REJECTED) continue;
      out.push(cell.data == null ? cell : { ...cell, data });
    }
    read.end();
    return out;
  }

  /** Re-evaluates all subscribers. A renderer-virtual row's handle forwards
   *  its source's `onChange` here (`bindSourceHandle`) so subscriptions on
   *  the renderer's `CellHandle.onData` re-evaluate when the source
   *  updates. */
  #fireListeners(): void {
    this.#subscriptions.fire(null);
  }

  #fireListenersInRange(range: NaiveDate.Range): void {
    this.#subscriptions.fire(range);
  }

  #refreshActiveFormulaRanges(): boolean {
    if (!this.computeRange) return false;
    const ranges = this.#subscriptions.watchedRanges();
    if (ranges.length === 0) return false;
    for (const range of ranges) {
      void this.refresh(range).catch((err) => {
        Tracing.dev("row-handle/active-formula-refresh-error", {
          rowId: this.rowId.toString(),
          err: err instanceof Error ? err.message : String(err),
        });
      });
    }
    return true;
  }

  static #naiveRangeFromUtc(
    range: DateTime.Range<Utc>,
  ): NaiveDate.Range {
    return new NaiveDate.Range(
      NaiveDate.fromDse(range.start.dse),
      NaiveDate.fromDse(range.end.dse),
    );
  }

  /** Re-entrancy witness — `rebuildAllIndices` is called from
   *  several paths (fetch completion, invalidation, mutations). If
   *  a listener fired during rebuild re-triggers another rebuild,
   *  the witness emits so the operator can see the cascade. */
  static readonly #rebuildReentrancy = new Tracing.Witness.Reentrancy({
    name: "row-handle/rebuild-indices",
    onReentry: "log",
  });

  /**
   * The row's cell set changed over `range` (`null`: any day): rebuild every
   * index factory's index from the whole set, and tell the rebuild listeners
   * the change. The whole set is read only when a factory or a listener asks
   * for it (`RowCellsChange.cells`).
   */
  #rebuildAllIndices(range: Option<NaiveDate.Range> = null): void {
    this.#indicesStale = false;
    const listening = this.#subscriptions.hasRebuildListeners;
    if (this.#factories.size === 0 && !listening) return;
    const exitReentry = RowCellCache.#rebuildReentrancy.enter({
      rowId: this.rowId.toString(),
      factoryCount: this.#factories.size,
    });
    try {
      const self = this as RowCellCache<Record<string, unknown>>;
      const authority = this.#authority;
      let all: Option<CellEntry[]> = null;
      const cells = (): CellEntry[] => all ??= authority.indexCells();
      for (const [key, factory] of this.#factories) {
        this.#indices[key] = factory(self, cells());
      }
      if (listening) {
        this.#subscriptions.fireRebuilt({ range, cells, cellsIn: (within) => authority.indexCellsIn(within) });
      }
    } finally {
      exitReentry();
    }
  }

  #rebuildIndex(key: string): void {
    const factory = this.#factories.get(key);
    if (!factory) return;
    const self = this as RowCellCache<Record<string, unknown>>;
    const cells = this.#authority.indexCells();
    this.#indices[key] = factory(self, cells);
  }
}

/**
 * A keyed read's lanes (`FetchedCells`, the sheets `Backend`'s
 * `fetchCellsByKey`) as the wire cells `RowCellCache` ingests: a date cell at
 * its partial date, a datetime cell over its range. Values pass through as
 * the server sent them (`Json.wire` is the identity on wire JSON);
 * `RowCellCache.decodeCells` decodes them. The keyed read of `RowFetchPlan`
 * and `SdkBackendAdapter.fetchRowCells`.
 */
export function fetchedAsWireCells(fetched: FetchedCells): Network.SheetCell[] {
  const out: Network.SheetCell[] = [];
  for (const cell of fetched.cells) {
    out.push(Network.SheetCellSchema.make({
      id: cell.id ?? null,
      date: SemanticTime.fromPartialDate(cell.date),
      endDate: null,
      data: Json.wire(cell.value),
    }));
  }
  for (const cell of fetched.custom) {
    out.push(Network.SheetCellSchema.make({
      id: cell.id,
      date: SemanticTime.fromDateTime(cell.dtr.start),
      endDate: SemanticTime.fromDateTime(cell.dtr.end),
      data: Json.wire(cell.value),
    }));
  }
  return out;
}

export namespace RowCellCache {
  /** The store view a holding sheet installs (`setView`): a `CellStoreView`, named by shape here (sync does not import cells). */
  export type View = HeldCellStore;
  /** A date-keyed cell (`CellEntry`): the value for a day/week/month/year column. */
  export type DateCell = CellEntry & { readonly kind: "date" };
  /** A timepoint cell (`TimepointCellEntry`): a value spanning a `DateTime` range. */
  export type TimepointCell = TimepointCellEntry & { readonly kind: "timepoint" };
  /** One entry of `RowCellCache.cellsIn` / `allCells`, tagged by `kind`. */
  export type Cell = DateCell | TimepointCell;
  /**
   * What a server read changed for its holder (`receiveBatchDataSince`):
   * `"changed"`, the row's cells changed (or a client-owned row's days were
   * first covered by a read that carried cells); `"covered"`, a client-owned
   * row's days were first covered by a read that carried none; `null`,
   * nothing.
   */
  export type Receipt = "changed" | "covered" | null;
}
